// src/App.js

import React, { useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import AuthGuard from "./AuthGuard";

// UI
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import CollegeSearchModal from "./components/CollegeSearchModal";
import Colleges from "./components/Colleges";
import ComparisonPage from "./components/ComparisonPage";
import { CompareProvider } from "./context/CompareContext";

// Pages
import HomePage from "./components/HomePage";
import LoginPage from "./components/LoginPage";
import AdminLogin from "./components/AdminLogin";
import RegistrationPage from "./components/RegistrationPage";
import ProfilePage from "./components/ProfilePage";
import CollegeDetail from "./components/CollegeDetail";
import PredictionBox from "./components/PredictionBox";
import Contact from "./components/Contact";
import TermsConditions from "./components/TermsConditions";
import DocumentChecklist from "./components/DocumentChecklist";
import CalendarPage from "./components/CalendarPage";
import StudentCalendar from "./components/StudentCalendar";
import NonCapAdmissions from "./components/NonCapAdmissions";
import PreferenceFormPage from "./components/preference_form";
import UserMessages from "./components/UserMessages";
import VideoPlayer from "./components/VideoPlayer";

// Counselling
import Counselling from "./components/Counselling";
import CounsellingProgram from "./components/CounsellingProgram";

// Android Counselling
import CounselingHome from "./components/counsellingPage/CounselingHome";
import EfcSubjects from "./components/counsellingPage/EfcSubjects";
import EfcVideos from "./components/counsellingPage/EfcVideos";
import AdmissionVideos from "./components/counsellingPage/AdmissionVideos";
import LiveVideos from "./components/counsellingPage/LiveVideos";

// Context
import { FavoritesProvider } from "./components/FavoritesContext";
import { SearchModalContext } from "./context/SearchModalContext";

// Admin
import AdminHome from "./components/AdminHome";
import AdminPanel from "./components/AdminPanel";
import AdminCalendar from "./components/AdminCalendar";
import ManageHomepageBanner from "./components/ManageHomepageBanner";

import "./App.css";

// ---------------- ADMIN GUARD ----------------

function AdminRoute({ children }) {
  const admin = localStorage.getItem("adminToken");
  return admin ? children : <Navigate to="/admin-login" />;
}

// ---------------- APP CONTENT ----------------

function AppContent() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const location = useLocation();

  const isAuthPage =
    location.pathname === "/login" ||
    location.pathname === "/admin-login";

  return (
    // ✅ PROVIDER MOVED HERE (CORRECT PLACE)
    <SearchModalContext.Provider
      value={{
        openSearch: () => setSearchOpen(true),
        closeSearch: () => setSearchOpen(false),
      }}
    >
      <>
        {/* HEADER + SIDEBAR */}
        {!isAuthPage && (
          <>
            <Header
              onMenuClick={() => setSidebarOpen(true)}
              onSearchClick={() => setSearchOpen(true)}
            />

            <Sidebar
              open={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
              onSearchOpen={() => setSearchOpen(true)}
            />
          </>
        )}

        {/* ROUTES */}
        <Routes>

          {/* DEFAULT */}
          <Route path="/" element={<Navigate to="/login" />} />

          {/* AUTH */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/register" element={<RegistrationPage />} />

          {/* HOME */}
          <Route
            path="/home"
            element={
              <AuthGuard>
                <HomePage />
              </AuthGuard>
            }
          />

          {/* PROFILE */}
          <Route
            path="/profile"
            element={
              <AuthGuard>
                <ProfilePage />
              </AuthGuard>
            }
          />

          <Route
            path="/colleges"
            element={
              <AuthGuard>
                <Colleges />
              </AuthGuard>
            }
          />

          <Route path="/terms" element={<TermsConditions />} />

          {/* ANDROID COUNSELLING */}
          <Route
            path="/counseling"
            element={
              <AuthGuard>
                <CounselingHome />
              </AuthGuard>
            }
          />

          <Route
            path="/efc-subjects"
            element={
              <AuthGuard>
                <EfcSubjects />
              </AuthGuard>
            }
          />

          <Route
            path="/efc-videos/:subject"
            element={
              <AuthGuard>
                <EfcVideos />
              </AuthGuard>
            }
          />

          <Route
            path="/admission-videos"
            element={
              <AuthGuard>
                <AdmissionVideos />
              </AuthGuard>
            }
          />

          <Route
            path="/live-videos"
            element={
              <AuthGuard>
                <LiveVideos />
              </AuthGuard>
            }
          />

          <Route path="/player" element={<VideoPlayer />} />

          {/* MAIN FEATURES */}

          <Route
            path="/college/:instituteCode"
            element={
              <AuthGuard>
                <CollegeDetail />
              </AuthGuard>
            }
          />

          <Route path="/compare" element={<ComparisonPage />} />

          <Route
            path="/predict"
            element={
              <AuthGuard>
                <PredictionBox />
              </AuthGuard>
            }
          />

          {/* COUNSELLING */}
          <Route
            path="/about-counselling"
            element={
              <AuthGuard>
                <Counselling />
              </AuthGuard>
            }
          />

          <Route
            path="/counselling"
            element={
              <AuthGuard>
                <Counselling />
              </AuthGuard>
            }
          />

          <Route
            path="/counselling-program"
            element={
              <AuthGuard>
                <CounsellingProgram />
              </AuthGuard>
            }
          />

          <Route
            path="/contact"
            element={
              <AuthGuard>
                <Contact />
              </AuthGuard>
            }
          />

          <Route
            path="/document-checklist"
            element={
              <AuthGuard>
                <DocumentChecklist />
              </AuthGuard>
            }
          />

          <Route path="/non-cap-admissions" element={<NonCapAdmissions />} />
          <Route path="/preference-form" element={<PreferenceFormPage />} />
          <Route path="/messages" element={<UserMessages />} />

          <Route
            path="/calendar"
            element={
              <AuthGuard>
                <CalendarPage />
              </AuthGuard>
            }
          />

          <Route
            path="/student/calendar"
            element={
              <AuthGuard>
                <StudentCalendar />
              </AuthGuard>
            }
          />

          {/* ADMIN */}

          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminHome />
              </AdminRoute>
            }
          >
            <Route path="manage-upload" element={<AdminPanel />} />
            <Route path="calendar" element={<AdminCalendar />} />
            <Route path="homepage-banner" element={<ManageHomepageBanner />} />
          </Route>

        </Routes>

        {/* SEARCH MODAL */}
        {!isAuthPage && (
          <CollegeSearchModal
            isOpen={searchOpen}
            onClose={() => setSearchOpen(false)}
          />
        )}
      </>
    </SearchModalContext.Provider>
  );
}

// ---------------- ROOT ----------------

function App() {
  return (
    <Router>
      <CompareProvider>
        <FavoritesProvider>
          <div className="App">
            <AppContent />
          </div>
        </FavoritesProvider>
      </CompareProvider>
    </Router>
  );
}

export default App;
